import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, LogOut, ListPlus, Image, X, AlertTriangle, Lock, Unlock } from 'lucide-react';
import { authService } from '../services/auth';
import { questionsService } from '../services/questions';
import { settingsService } from '../services/settings';
import { Question, Settings } from '../types';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';
import { Card } from './ui/Card';

export function AdminPanel() {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [bulkQuestions, setBulkQuestions] = useState('');
  const [settings, setSettings] = useState<Settings>({ logoUrl: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPinInput, setShowPinInput] = useState(false);
  const [pin, setPin] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      if (!authService.isAuthenticated()) {
        navigate('/login');
      }
    };
    checkAuth();
    loadQuestions();
    loadSettings();
  }, [navigate]);

  const loadQuestions = async () => {
    try {
      setError(null);
      const allQuestions = await questionsService.getAll();
      setQuestions(allQuestions);
    } catch (error: any) {
      console.error('Error loading questions:', error);
      setError(error?.message || 'Erreur lors du chargement des questions');
    }
  };

  const loadSettings = async () => {
    try {
      setError(null);
      const currentSettings = await settingsService.get();
      setSettings(currentSettings);
    } catch (error: any) {
      console.error('Error loading settings:', error);
      setError(error?.message || 'Erreur lors du chargement des paramètres');
    }
  };

  const handleAddSingleQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newQuestion.trim()) return;

    setIsLoading(true);
    setError(null);
    try {
      await questionsService.add(newQuestion.trim());
      await loadQuestions();
      setNewQuestion('');
    } catch (error: any) {
      console.error('Error adding question:', error);
      setError(error?.message || 'Erreur lors de l\'ajout de la question');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBulkQuestions = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkQuestions.trim()) return;

    setIsLoading(true);
    setError(null);
    try {
      const questionsArray = bulkQuestions
        .split('\n')
        .map(q => q.trim())
        .filter(q => q.length > 0);

      for (const questionContent of questionsArray) {
        await questionsService.add(questionContent);
      }

      await loadQuestions();
      setBulkQuestions('');
    } catch (error: any) {
      console.error('Error adding bulk questions:', error);
      setError(error?.message || 'Erreur lors de l\'ajout des questions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteQuestion = async (id: string) => {
    try {
      setError(null);
      await questionsService.delete(id);
      await loadQuestions();
    } catch (error: any) {
      console.error('Error deleting question:', error);
      setError(error?.message || 'Erreur lors de la suppression de la question');
    }
  };

  const handleDeleteAllQuestions = async () => {
    try {
      setError(null);
      await questionsService.deleteAll();
      await loadQuestions();
      setShowDeleteConfirm(false);
    } catch (error: any) {
      console.error('Error deleting all questions:', error);
      setError(error?.message || 'Erreur lors de la suppression des questions');
    }
  };

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      navigate('/login');
    } catch (error: any) {
      console.error('Error signing out:', error);
      setError(error?.message || 'Erreur lors de la déconnexion');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('L\'image ne doit pas dépasser 2MB');
      return;
    }

    try {
      setError(null);
      const updatedSettings = await settingsService.setLogo(file);
      setSettings(updatedSettings);
    } catch (error: any) {
      console.error('Error updating logo:', error);
      setError(error?.message || 'Erreur lors du changement du logo');
    }
  };

  const handleRemoveLogo = async () => {
    try {
      setError(null);
      const updatedSettings = await settingsService.setLogo(null);
      setSettings(updatedSettings);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error removing logo:', error);
      setError(error?.message || 'Erreur lors de la suppression du logo');
    }
  };

  const handleToggleProtection = async () => {
    try {
      setError(null);
      if (!settings.isProtected) {
        setShowPinInput(true);
      } else {
        await settingsService.update({
          ...settings,
          isProtected: false,
          pin: null
        });
        await loadSettings();
      }
    } catch (error: any) {
      console.error('Error toggling protection:', error);
      setError(error?.message || 'Erreur lors de la modification de la protection');
    }
  };

  const handlePinSubmit = async (newPin: string) => {
    try {
      setError(null);
      await settingsService.update({
        ...settings,
        isProtected: true,
        pin: newPin
      });
      setShowPinInput(false);
      await loadSettings();
    } catch (error: any) {
      console.error('Error setting PIN:', error);
      setError(error?.message || 'Erreur lors de la définition du code PIN');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <div className="flex justify-between items-center mb-8">
            <div className="flex items-center">
              <ListPlus className="w-8 h-8 text-indigo-600 mr-4" />
              <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Panneau d'Administration
              </h1>
            </div>
            <Button
              variant="danger"
              onClick={handleSignOut}
              icon={<LogOut className="w-4 h-4" />}
            >
              Déconnexion
            </Button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-start">
              <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4 text-gray-700">
              Paramètres
            </h2>
            <div className="space-y-4">
              {/* Protection toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {settings.isProtected ? (
                    <Lock className="w-5 h-5 text-indigo-600" />
                  ) : (
                    <Unlock className="w-5 h-5 text-gray-400" />
                  )}
                  <div>
                    <h3 className="font-medium text-gray-900">Protection par code</h3>
                    <p className="text-sm text-gray-500">
                      {settings.isProtected
                        ? 'La page est protégée par un code PIN'
                        : 'La page est accessible sans code'}
                    </p>
                  </div>
                </div>
                <Button
                  variant={settings.isProtected ? 'danger' : 'primary'}
                  onClick={handleToggleProtection}
                  icon={settings.isProtected ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                >
                  {settings.isProtected ? 'Désactiver' : 'Activer'}
                </Button>
              </div>

              {/* Logo management */}
              {settings.logoUrl && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={settings.logoUrl} 
                    alt="Logo actuel" 
                    className="h-12 object-contain"
                  />
                  <Button
                    variant="danger"
                    onClick={handleRemoveLogo}
                    icon={<X className="w-4 h-4" />}
                  >
                    Supprimer le logo
                  </Button>
                </div>
              )}
              <div className="flex gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  id="logo-upload"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  icon={<Image className="w-5 h-5" />}
                  className="w-full"
                >
                  {settings.logoUrl ? 'Changer le logo' : 'Ajouter un logo'}
                </Button>
              </div>
              <p className="text-sm text-gray-500">
                Format accepté : JPG, PNG, GIF (max 2MB)
              </p>
            </div>
          </div>

          <div className="space-y-8 mb-8">
            <div>
              <h2 className="text-lg font-semibold mb-4 text-gray-700">
                Ajouter une question
              </h2>
              <form onSubmit={handleAddSingleQuestion} className="flex gap-4">
                <input
                  type="text"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                  placeholder="Entrez une nouvelle question..."
                  className="flex-1 p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  icon={<Plus className="w-5 h-5" />}
                  isLoading={isLoading}
                >
                  Ajouter
                </Button>
              </form>
            </div>

            <div>
              <h2 className="text-lg font-semibold mb-4 text-gray-700">
                Ajouter plusieurs questions
              </h2>
              <form onSubmit={handleAddBulkQuestions} className="space-y-4">
                <textarea
                  value={bulkQuestions}
                  onChange={(e) => setBulkQuestions(e.target.value)}
                  placeholder="Collez vos questions (une par ligne)..."
                  className="w-full p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 min-h-[150px]"
                  disabled={isLoading}
                />
                <Button
                  type="submit"
                  icon={<Plus className="w-5 h-5" />}
                  isLoading={isLoading}
                >
                  Ajouter les questions
                </Button>
              </form>
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-700">
                Questions ({questions.length})
              </h2>
              {questions.length > 0 && (
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  Tout supprimer
                </Button>
              )}
            </div>

            {showDeleteConfirm && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-red-800 font-medium">
                      Êtes-vous sûr de vouloir supprimer toutes les questions ?
                    </h3>
                    <p className="text-red-600 text-sm mt-1">
                      Cette action est irréversible.
                    </p>
                    <div className="mt-3 flex space-x-3">
                      <Button
                        variant="danger"
                        onClick={handleDeleteAllQuestions}
                        size="sm"
                      >
                        Oui, tout supprimer
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        size="sm"
                      >
                        Annuler
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <ul className="space-y-4">
              {questions.map((question) => (
                <li
                  key={question.id}
                  className="group flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-colors duration-200"
                >
                  <span className="text-gray-700 group-hover:text-indigo-700 transition-colors duration-200">
                    {question.content}
                  </span>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteQuestion(question.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    icon={<Trash2 className="w-4 h-4" />}
                  >
                    Supprimer
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      </div>

      {showPinInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Définir le code PIN
              </h2>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 4) setPin(value);
                  }}
                  className="flex-1 p-4 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Code à 4 chiffres"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setShowPinInput(false)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={() => handlePinSubmit(pin)}
                  className="flex-1"
                  disabled={pin.length !== 4}
                >
                  Valider
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}